import type { ReactNode } from 'react'
import {
  BrainCircuit20Regular,
  Database20Regular,
  DocumentSearch20Regular,
  Fingerprint20Regular,
  Key20Regular,
  Laptop20Regular,
  LockClosed20Regular,
  ScanObject20Regular,
  Server20Regular,
} from '@fluentui/react-icons'

export type DeploymentMode = 'local' | 'hybrid'

type Node = { icon: ReactNode; name: string; role: string; tech: string }

const endpointNodes: Record<DeploymentMode, Node[]> = {
  local: [
    { icon: <Laptop20Regular />, name: 'Libris Desktop', role: 'Search, details and file actions', tech: 'Avalonia · .NET' },
    { icon: <Fingerprint20Regular />, name: 'Libris Agent', role: 'Watching, parsing, OCR, embeddings', tech: 'Background service' },
    { icon: <Database20Regular />, name: 'Local catalog', role: 'Metadata, text and vectors', tech: 'SQLite · FTS5' },
    { icon: <ScanObject20Regular />, name: 'OCR runtime', role: 'English and Greek extraction', tech: 'Tesseract · eng+ell' },
  ],
  hybrid: [
    { icon: <Laptop20Regular />, name: 'Libris Desktop', role: 'One search across every source', tech: 'OIDC client' },
    { icon: <Fingerprint20Regular />, name: 'Libris Agent', role: 'Local index and approved sync', tech: 'mTLS endpoint' },
    { icon: <Database20Regular />, name: 'Local catalog', role: 'Offline metadata and vectors', tech: 'SQLite · FTS5' },
  ],
}

const serverNodes: Node[] = [
  { icon: <Server20Regular />, name: 'Libris Server', role: 'Search, policy and governance APIs', tech: 'ASP.NET Core' },
  { icon: <Database20Regular />, name: 'PostgreSQL', role: 'Central catalog, audit and vectors', tech: 'pgvector · HNSW' },
  { icon: <Key20Regular />, name: 'Keycloak', role: 'Users, roles and authentication', tech: 'OIDC · PKCE' },
  { icon: <DocumentSearch20Regular />, name: 'Shared roots', role: 'File-server folders indexed directly', tech: 'SMB · NTFS ACL' },
]

function SystemNode({ node, index }: { node: Node; index: number }) {
  return (
    <div className="topo-node" style={{ ['--i' as string]: index }}>
      <span className="topo-node-icon">{node.icon}</span>
      <span className="topo-node-text">
        <strong>{node.name}</strong>
        <small>{node.role}</small>
        <em className="mono">{node.tech}</em>
      </span>
    </div>
  )
}

export function Topology({ mode }: { mode: DeploymentMode }) {
  const hybrid = mode === 'hybrid'

  return (
    <div className="topo" data-mode={mode} key={mode}>
      <section className="topo-tier">
        <header className="topo-tier-head mono">
          <span>Windows workstation</span>
          <small>{hybrid ? '10–20 enrolled PCs' : 'One PC · no server dependency'}</small>
        </header>
        <div className="topo-nodes" data-count={endpointNodes[mode].length}>
          {endpointNodes[mode].map((node, index) => (
            <SystemNode key={node.name} node={node} index={index} />
          ))}
        </div>
      </section>

      {hybrid && (
        <div className="topo-link" aria-hidden="true">
          <span className="topo-link-label mono">
            <LockClosed20Regular />
            HTTPS · mutual TLS · signed envelopes · policy-approved fields
          </span>
          <i className="packet" style={{ ['--d' as string]: '0s' }} />
          <i className="packet" style={{ ['--d' as string]: '1.1s' }} />
          <i className="packet packet-up" style={{ ['--d' as string]: '2.1s' }} />
        </div>
      )}

      {hybrid && (
        <section className="topo-tier topo-tier-server">
          <header className="topo-tier-head mono">
            <span>Office server machine</span>
            <small>Native Windows installation</small>
          </header>
          <div className="topo-nodes" data-count={4}>
            {serverNodes.map((node, index) => (
              <SystemNode key={node.name} node={node} index={index} />
            ))}
          </div>
        </section>
      )}

      <section className="topo-tier topo-tier-external">
        <header className="topo-tier-head mono">
          <span>Optional external boundary</span>
          <small>Only when an administrator enables it</small>
        </header>
        <div className="topo-nodes" data-count={1}>
          <SystemNode
            index={0}
            node={{
              icon: <BrainCircuit20Regular />,
              name: 'Embedding provider',
              role: 'Permitted extracted text only, never whole files',
              tech: 'HTTPS · user-supplied key',
            }}
          />
        </div>
      </section>
    </div>
  )
}
